import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { 
  Gift, 
  Star, 
  TrendingUp, 
  ShoppingBag, 
  Percent, 
  History,
  CheckCircle,
  AlertCircle,
  Loader2,
  Info,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR').format(value) + ' FCFA';
};

const LoyaltyCard = ({ clientId: propClientId, clientName, clientPhone, onPointsUpdate }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState(propClientId);
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showEarnDialog, setShowEarnDialog] = useState(false);
  const [showUseDialog, setShowUseDialog] = useState(false);
  const [earnAmount, setEarnAmount] = useState('');
  const [earnParapharmacyAmount, setEarnParapharmacyAmount] = useState('');
  const [useAmount, setUseAmount] = useState('');
  const [discountPreview, setDiscountPreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [clientNotFound, setClientNotFound] = useState(false);

  // Find client by phone if clientId not provided
  const findClientByPhone = useCallback(async () => {
    if (propClientId || !clientPhone) return;
    
    try {
      const response = await axios.get(`${API_URL}/clients?search=${clientPhone}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const clients = response.data;
      const found = clients.find(c => c.phone === clientPhone);
      if (found) {
        setClientId(found.id);
      } else {
        setClientNotFound(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error finding client:', error);
      setClientNotFound(true);
      setLoading(false);
    }
  }, [propClientId, clientPhone, token]);

  useEffect(() => {
    if (propClientId) {
      setClientId(propClientId);
    } else if (clientPhone) {
      findClientByPhone();
    } else {
      setLoading(false);
    }
  }, [propClientId, clientPhone, findClientByPhone]);

  const fetchLoyaltyInfo = useCallback(async () => {
    if (!clientId) return;
    
    try {
      const response = await axios.get(`${API_URL}/loyalty/client/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoyaltyInfo(response.data);
    } catch (error) {
      console.error('Error fetching loyalty info:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/loyalty/history/${clientId}?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchLoyaltyInfo();
    }
  }, [clientId, fetchLoyaltyInfo]);

  const handleEarnPoints = async () => {
    if (!earnAmount || !earnParapharmacyAmount) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    const total = parseInt(earnAmount);
    const parapharmacy = parseInt(earnParapharmacyAmount);

    if (parapharmacy > total) {
      toast.error('Le montant parapharmacie ne peut pas dépasser le total');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/loyalty/earn`, {
        client_id: clientId,
        purchase_amount: total,
        parapharmacy_amount: parapharmacy
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message);
      setShowEarnDialog(false);
      setEarnAmount('');
      setEarnParapharmacyAmount('');
      fetchLoyaltyInfo();
      if (onPointsUpdate) onPointsUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setProcessing(false);
    }
  };

  const previewDiscount = useCallback(async () => {
    if (!useAmount) return;

    try {
      const response = await axios.post(
        `${API_URL}/loyalty/calculate-discount?client_id=${clientId}&parapharmacy_amount=${parseInt(useAmount)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDiscountPreview(response.data);
    } catch (error) {
      console.error('Error calculating discount:', error);
    }
  }, [useAmount, clientId, token]);

  useEffect(() => {
    if (useAmount && parseInt(useAmount) > 0) {
      const timeout = setTimeout(previewDiscount, 300);
      return () => clearTimeout(timeout);
    } else {
      setDiscountPreview(null);
    }
  }, [useAmount, previewDiscount]);

  const handleUseDiscount = async () => {
    if (!useAmount || !discountPreview?.can_apply_discount) {
      toast.error('Impossible d\'appliquer la remise');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/loyalty/use-discount`, {
        client_id: clientId,
        parapharmacy_amount: parseInt(useAmount),
        points_to_use: discountPreview.points_to_debit
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message);
      setShowUseDialog(false);
      setUseAmount('');
      setDiscountPreview(null);
      fetchLoyaltyInfo();
      if (onPointsUpdate) onPointsUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'utilisation');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-[#E8DDD4]">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#C9A086]" />
        </CardContent>
      </Card>
    );
  }

  if (clientNotFound) {
    return (
      <Card className="border-[#E8DDD4] bg-amber-50">
        <CardContent className="p-6 text-center">
          <Gift className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <p className="text-sm text-amber-700 font-medium">Client non enregistré</p>
          <p className="text-xs text-amber-600 mt-1">
            Ce client n'a pas encore de compte fidélité.<br/>
            Créez sa fiche client pour activer la fidélité.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!loyaltyInfo) {
    return null;
  }

  // Calculate progress to next tier
  const progressToNext = loyaltyInfo.next_tier_points 
    ? Math.min(100, ((loyaltyInfo.total_points / loyaltyInfo.next_tier_points) * 100))
    : 100;

  return (
    <>
      <Card className="border-[#E8DDD4] bg-gradient-to-br from-amber-50 to-orange-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium text-[#5D4E37] flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-600" />
            Programme Fidélité
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Points Balance */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-amber-700">
                {loyaltyInfo.total_points}
                <span className="text-lg font-normal text-amber-600 ml-1">points</span>
              </p>
              <p className="text-sm text-[#78716C]">{loyaltyInfo.client_name}</p>
            </div>
            {loyaltyInfo.can_use_discount && (
              <Badge className="bg-green-100 text-green-700 text-lg px-3 py-1">
                <Percent className="w-4 h-4 mr-1" />
                {loyaltyInfo.available_discount_percent}%
              </Badge>
            )}
          </div>

          {/* Progress to next tier */}
          {loyaltyInfo.next_tier_points && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-[#78716C]">
                <span>Prochain palier: {loyaltyInfo.next_tier_discount}%</span>
                <span>{loyaltyInfo.points_to_next_tier} points restants</span>
              </div>
              <Progress value={progressToNext} className="h-2 bg-amber-100" />
            </div>
          )}

          {/* Status Message */}
          <div className={`p-3 rounded-lg flex items-start gap-2 ${
            loyaltyInfo.can_use_discount 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-amber-50 border border-amber-200'
          }`}>
            {loyaltyInfo.can_use_discount ? (
              <Sparkles className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            )}
            <p className={`text-sm ${loyaltyInfo.can_use_discount ? 'text-green-700' : 'text-amber-700'}`}>
              {loyaltyInfo.message}
            </p>
          </div>

          {/* Discount Tiers Info */}
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className={`p-2 rounded ${loyaltyInfo.total_points >= 100 ? 'bg-amber-200' : 'bg-gray-100'}`}>
              <p className="font-bold">100 pts</p>
              <p className="text-[#78716C]">2%</p>
            </div>
            <div className={`p-2 rounded ${loyaltyInfo.total_points >= 200 ? 'bg-amber-200' : 'bg-gray-100'}`}>
              <p className="font-bold">200 pts</p>
              <p className="text-[#78716C]">3%</p>
            </div>
            <div className={`p-2 rounded ${loyaltyInfo.total_points >= 300 ? 'bg-amber-200' : 'bg-gray-100'}`}>
              <p className="font-bold">300 pts</p>
              <p className="text-[#78716C]">5%</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button 
              onClick={() => setShowEarnDialog(true)}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              size="sm"
            >
              <ShoppingBag className="w-4 h-4 mr-1" />
              Enregistrer achat
            </Button>
            {loyaltyInfo.can_use_discount && (
              <Button 
                onClick={() => setShowUseDialog(true)}
                variant="outline"
                className="flex-1 border-green-500 text-green-600 hover:bg-green-50"
                size="sm"
              >
                <Percent className="w-4 h-4 mr-1" />
                Utiliser remise
              </Button>
            )}
          </div>

          {/* History Link */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-[#78716C]"
            onClick={() => {
              fetchHistory();
              setShowHistory(true);
            }}
          >
            <History className="w-4 h-4 mr-1" />
            Voir l'historique
          </Button>

          {/* Info */}
          <p className="text-[10px] text-[#78716C] text-center">
            Remise applicable uniquement sur les produits parapharmacie
          </p>
        </CardContent>
      </Card>

      {/* Earn Points Dialog */}
      <Dialog open={showEarnDialog} onOpenChange={setShowEarnDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-amber-600" />
              Enregistrer un achat
            </DialogTitle>
            <DialogDescription>
              Saisissez le montant de l'achat pour créditer les points fidélité
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Montant total de l'achat (FCFA) *</Label>
              <Input 
                type="number"
                placeholder="Ex: 25000"
                value={earnAmount}
                onChange={(e) => setEarnAmount(e.target.value)}
              />
            </div>
            <div>
              <Label>Dont parapharmacie (FCFA) *</Label>
              <Input 
                type="number"
                placeholder="Ex: 18000"
                value={earnParapharmacyAmount}
                onChange={(e) => setEarnParapharmacyAmount(e.target.value)}
              />
              <p className="text-xs text-[#78716C] mt-1">
                Seul le montant parapharmacie génère des points (1 pt / 1000 FCFA)
              </p>
            </div>
            {earnParapharmacyAmount && parseInt(earnParapharmacyAmount) >= 1000 && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700">
                  <TrendingUp className="w-4 h-4 inline mr-1" />
                  Points à créditer: <strong>+{Math.floor(parseInt(earnParapharmacyAmount) / 1000)} points</strong>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEarnDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleEarnPoints} 
              disabled={processing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Créditer les points
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use Discount Dialog */}
      <Dialog open={showUseDialog} onOpenChange={setShowUseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-green-600" />
              Utiliser la remise fidélité
            </DialogTitle>
            <DialogDescription>
              Remise de {loyaltyInfo?.available_discount_percent}% disponible ({loyaltyInfo?.total_points} points)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Montant parapharmacie du panier (FCFA) *</Label>
              <Input 
                type="number"
                placeholder="Ex: 15000"
                value={useAmount}
                onChange={(e) => setUseAmount(e.target.value)}
              />
              <p className="text-xs text-[#78716C] mt-1">
                La remise ne s'applique PAS aux médicaments
              </p>
            </div>

            {discountPreview && (
              <div className={`p-4 rounded-lg border ${
                discountPreview.can_apply_discount 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                {discountPreview.can_apply_discount ? (
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-[#5D4E37]">Montant parapharmacie:</span>
                      <span className="font-medium">{formatCurrency(parseInt(useAmount))}</span>
                    </div>
                    <div className="flex justify-between text-green-700">
                      <span className="text-sm">Remise ({discountPreview.discount_percent}%):</span>
                      <span className="font-bold">-{formatCurrency(discountPreview.discount_amount)}</span>
                    </div>
                    <div className="border-t border-green-300 pt-2 flex justify-between">
                      <span className="text-sm font-medium">Montant final:</span>
                      <span className="font-bold text-green-800">{formatCurrency(discountPreview.final_amount)}</span>
                    </div>
                    <p className="text-xs text-green-600 mt-2">
                      {discountPreview.points_to_debit} points seront débités → Nouveau solde: {discountPreview.points_after_discount} points
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-4 h-4" />
                    <p className="text-sm">{discountPreview.message}</p>
                  </div>
                )}
              </div>
            )}

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700">
                <strong>Rappel :</strong> Appliquez manuellement cette remise dans Mediciel, 
                puis confirmez ici pour débiter les points.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUseDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleUseDiscount} 
              disabled={processing || !discountPreview?.can_apply_discount}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Confirmer et débiter les points
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#C9A086]" />
              Historique fidélité
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {history.length > 0 ? (
              <div className="space-y-2">
                {history.map((tx, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      tx.transaction_type === 'earn' 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className={`text-sm font-medium ${
                          tx.transaction_type === 'earn' ? 'text-green-700' : 'text-orange-700'
                        }`}>
                          {tx.transaction_type === 'earn' ? '+' : ''}{tx.points} points
                        </p>
                        <p className="text-xs text-[#78716C] mt-1">{tx.description}</p>
                      </div>
                      <span className="text-xs text-[#78716C]">
                        {new Date(tx.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-[#78716C] py-8">
                Aucune transaction
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoyaltyCard;
